/**
 * OnboardingForm - Multi-tab form for merchant onboarding
 * Refactored for better maintainability and reduced duplication
 */

import { FC, useState, useCallback, useEffect } from "react";
import {
  Button,
  Center,
  Group,
  Loader,
  ScrollArea,
  Space,
  Tabs,
  Title,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { OnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { TAB_CONFIGS, getInitialDirtyState } from "./tabConfig";
import { UnsavedChangesModal } from "./components/UnsavedChangesModal";
import { STYLES, ROUTES } from "./constants";
import { RootState } from "../../store";
import useGetOnboardingStatus from "../../hooks/useGetOnboardingStatus";
import { toast } from "react-toastify";

interface OnboardingFormProps {
  onboardingStatus: OnboardingStatusEnum;
  merchantName: string;
  merchantEmail: string;
  merchantId?: number;
}

const OnboardingForm: FC<OnboardingFormProps> = ({
  onboardingStatus: initialOnboardingStatus,
  merchantName,
  merchantEmail,
  merchantId,
}) => {
  const navigate = useNavigate();
  const { token } = useSelector((state: RootState) => state.auth);

  // Use refactored hook with built-in polling
  const {
    data: statusData,
    isLoading,
    error,
  } = useGetOnboardingStatus({
    userId: merchantId ?? null,
    token,
    enabled: !!merchantId && !!token,
  });

  // Use fetched status if available, fallback to initial prop
  const currentStatus = statusData?.status ?? initialOnboardingStatus;

  const [activeTab, setActiveTab] = useState(0);
  const [isDirty, setIsDirty] = useState(getInitialDirtyState());
  const [pendingTab, setPendingTab] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);
  const [saveDraftCallbacks, setSaveDraftCallbacks] = useState<{
    [key: number]: () => void;
  }>({});
  const [resetFormCallbacks, setResetFormCallbacks] = useState<{
    [key: number]: () => void;
  }>({});

  // Show toast notification when status changes
  useEffect(() => {
    if (statusData && statusData.status !== initialOnboardingStatus) {
      const isNowEditable =
        statusData.status === OnboardingStatusEnum.ONBOARDING_IN_DRAFT ||
        statusData.status === OnboardingStatusEnum.ONBOARDING_RESUBMISSION_IN_DRAFT;

      toast.info(
        `Onboarding status changed to "${statusData.status}". Form is now ${
          isNowEditable ? "editable" : "locked"
        }.`,
        {
          autoClose: 5000,
          toastId: `status-change-${statusData.status}`, // Prevent duplicate toasts
        }
      );
    }
  }, [statusData, initialOnboardingStatus]);

  // Show error toast if initial fetch fails
  useEffect(() => {
    if (error && isLoading === false) {
      toast.error(`Failed to fetch onboarding status: ${error}`);
    }
  }, [error, isLoading]);

  const isEditable =
    currentStatus === OnboardingStatusEnum.ONBOARDING_IN_DRAFT ||
    currentStatus === OnboardingStatusEnum.ONBOARDING_RESUBMISSION_IN_DRAFT;

  // Callback handlers for tab components
  const handleDirtyChange = useCallback((tabIndex: number, dirty: boolean) => {
    setIsDirty((prev) => ({ ...prev, [tabIndex]: dirty }));
  }, []);

  const handleSaveDraftCallback = useCallback(
    (tabIndex: number, callback: () => void) => {
      setSaveDraftCallbacks((prev) => ({ ...prev, [tabIndex]: callback }));
    },
    []
  );

  const handleResetFormCallback = useCallback(
    (tabIndex: number, callback: () => void) => {
      setResetFormCallbacks((prev) => ({ ...prev, [tabIndex]: callback }));
    },
    []
  );

  // Tab switching with unsaved changes check
  const handleTabChange = (value: string | null) => {
    const newTab = Number(value);
    if (isDirty[activeTab] && newTab !== activeTab) {
      setPendingTab(newTab);
      setShowSaveModal(true);
    } else {
      setActiveTab(newTab);
    }
  };

  // Unified handler for save and navigate actions
  const handleSaveAndNavigate = useCallback(
    (destination: "switch" | "back") => {
      if (saveDraftCallbacks[activeTab]) {
        saveDraftCallbacks[activeTab]();
      }
      setIsDirty((prev) => ({ ...prev, [activeTab]: false }));

      if (destination === "switch") {
        setActiveTab(pendingTab!);
        setShowSaveModal(false);
        setPendingTab(null);
      } else {
        setShowBackModal(false);
        navigate(ROUTES.ONBOARDING_LIST);
      }
    },
    [activeTab, pendingTab, saveDraftCallbacks, navigate]
  );

  // Unified handler for discard and navigate actions
  const handleDiscardAndNavigate = useCallback(
    (destination: "switch" | "back") => {
      if (resetFormCallbacks[activeTab]) {
        resetFormCallbacks[activeTab]();
      }
      setIsDirty((prev) => ({ ...prev, [activeTab]: false }));

      if (destination === "switch") {
        setActiveTab(pendingTab!);
        setShowSaveModal(false);
        setPendingTab(null);
      } else {
        setShowBackModal(false);
        navigate(ROUTES.ONBOARDING_LIST);
      }
    },
    [activeTab, pendingTab, resetFormCallbacks, navigate]
  );

  // Back button handler
  const handleBackClick = () => {
    const hasUnsavedChanges = Object.values(isDirty).some((dirty) => dirty);
    if (hasUnsavedChanges) {
      setShowBackModal(true);
    } else {
      navigate(ROUTES.ONBOARDING_LIST);
    }
  };

  if (isLoading) {
    return (
      <Center style={{ height: "100vh" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <div style={STYLES.container}>
      <Group position="apart" align="center" mb="lg">
        <Group align="center">
          <Button
            variant="subtle"
            onClick={handleBackClick}
            leftIcon={<IconArrowLeft size={18} />}
            color="blue"
          >
            Back
          </Button>
        </Group>
        <Title order={2} style={{ fontSize: "20px" }}>
          {merchantName}
        </Title>
      </Group>
      <Space h="md" />
      <Tabs
        value={activeTab.toString()}
        onTabChange={handleTabChange}
        variant="outline"
        styles={(theme) => ({
          tabsList: {
            backgroundColor: theme.white,
            borderBottom: `1px solid ${theme.colors.gray[3]}`,
            padding: theme.spacing.md,
            zIndex: 10,
          },
          tab: {
            padding: theme.spacing.md,
            fontWeight: 500,
            "&[data-active]": {
              borderBottomColor: theme.colors.blue[6],
            },
          },
          root: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
          },
        })}
      >
        <Tabs.List>
          {TAB_CONFIGS.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <ScrollArea style={STYLES.scrollArea} type="auto" offsetScrollbars>
          {TAB_CONFIGS.map((tab, index) => {
            const Component = tab.component;
            return (
              <Tabs.Panel key={tab.value} value={tab.value} pt="md">
                <Component
                  isEditable={isEditable}
                  merchantId={merchantId ?? 0}
                  {...(tab.requiresEmail && { merchantEmail })}
                  {...(tab.requiresOnboardingStatus && {
                    onboardingStatus: currentStatus,
                  })}
                  onDirtyChange={(dirty) => handleDirtyChange(index, dirty)}
                  onSaveDraft={(callback) =>
                    handleSaveDraftCallback(index, callback)
                  }
                  onResetForm={(callback) =>
                    handleResetFormCallback(index, callback)
                  }
                />
              </Tabs.Panel>
            );
          })}
        </ScrollArea>
      </Tabs>

      {/* Unified modal for tab switching */}
      <UnsavedChangesModal
        opened={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setPendingTab(null);
        }}
        onSave={() => handleSaveAndNavigate("switch")}
        onDiscard={() => handleDiscardAndNavigate("switch")}
        actionText="switching tabs"
        saveButtonText="Save and Switch"
        discardButtonText="Discard and Switch"
      />

      {/* Unified modal for back navigation */}
      <UnsavedChangesModal
        opened={showBackModal}
        onClose={() => setShowBackModal(false)}
        onSave={() => handleSaveAndNavigate("back")}
        onDiscard={() => handleDiscardAndNavigate("back")}
        actionText="going back"
        saveButtonText="Save and Go Back"
        discardButtonText="Discard and Go Back"
      />
    </div>
  );
};

export default OnboardingForm;
